import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FlowguardLogo } from "@/components/FlowguardLogo";
import { PUBLIC_SITE_URL, publicSitePath } from "@/utils/publicSite";
import { 
  Lock, CheckCircle, AlertCircle, Loader2, PartyPopper, Shield, Check
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompleteRegistrationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  
  // Get session_id or payment_intent and pending_id from URL
  const sessionId = searchParams.get('session_id') || searchParams.get('payment_intent');
  const pendingId = searchParams.get('pending_id');
  
  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [pendingData, setPendingData] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError("Invalid registration link. Please start the registration process again.");
      setLoading(false);
      return;
    }
    
    if (pendingId) {
      fetchPendingRegistration();
    } else {
      setLoading(false);
    }
  }, [sessionId, pendingId]);

  const fetchPendingRegistration = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/pending-registration/${pendingId}`);
      setPendingData(response.data);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Failed to verify registration. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const passwordRequirements = [
    { label: "At least 8 characters", met: password.length >= 8 },
    { label: "Contains a number", met: /\d/.test(password) },
    { label: "Passwords match", met: password === confirmPassword && password.length > 0 },
  ];

  const allRequirementsMet = passwordRequirements.every(req => req.met);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/payments/complete-registration`,
        {
          session_id: sessionId,
          password: password
        },
        { withCredentials: true }
      );

      // Keep the access token in memory; the refresh token is stored in an
      // HttpOnly cookie by the backend.
      const { access_token, user } = response.data;
      loginWithToken(access_token, user);
      
      setCompleted(true);
      toast.success("Account created successfully!");
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || "Failed to complete registration. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <PartyPopper className="w-8 h-8 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Welcome to FlowGuard!</h1>
            <p className="text-slate-400 mb-6">
              Your account has been created successfully. Redirecting to your dashboard...
            </p>
            <div className="flex items-center justify-center gap-2 text-emerald-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading dashboard...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !pendingData) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-slate-800/50 border border-red-500/30 rounded-2xl p-8">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Registration Error</h1>
            <p className="text-slate-400 mb-6">{error}</p>
            <Button
              onClick={() => navigate('/register')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 font-semibold"
            >
              Start Over
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <a href={PUBLIC_SITE_URL} className="flex items-center justify-center gap-2 mb-8">
          <FlowguardLogo className="h-10 w-10" showText={false} />
          <span className="text-2xl font-bold text-white font-['Manrope']">FlowGuard</span>
        </a>

        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
          {/* Success Icon */}
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>

          <h1 className="text-2xl font-bold text-white text-center mb-2">
            Payment Successful!
          </h1>
          <p className="text-slate-400 text-center mb-6">
            {pendingData ? (
              <>Welcome, <span className="text-white font-medium">{pendingData.name}</span>! Set your password to complete registration.</>
            ) : (
              <>Set your password to complete your registration.</>
            )}
          </p>

          {/* User Info */}
          {pendingData && (
            <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Email:</span>
                <span className="text-white">{pendingData.email}</span>
              </div>
              <div className="flex items-center justify-between text-sm mt-2">
                <span className="text-slate-400">Plan:</span>
                <span className="text-amber-500 font-medium capitalize">{pendingData.tier} Investor</span>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-slate-300">Create Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  required
                />
              </div>
            </div>

            {/* Password Requirements */}
            <div className="space-y-2">
              {passwordRequirements.map((req, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs">
                  {req.met ? (
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <div className="w-3.5 h-3.5 rounded-full border border-slate-600" />
                  )}
                  <span className={req.met ? 'text-emerald-400' : 'text-slate-500'}>
                    {req.label}
                  </span>
                </div>
              ))}
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-slate-900 font-semibold py-6 mt-6"
              disabled={submitting || !allRequirementsMet}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  <Shield className="mr-2 h-4 w-4" />
                  Complete Registration
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-slate-500 text-xs mt-6">
            By creating an account, you agree to our{' '}
            <a href={publicSitePath("/terms")} className="text-amber-500 hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href={publicSitePath("/privacy")} className="text-amber-500 hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompleteRegistrationPage;
